import React, { useState, useCallback } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import { FileSpreadsheet, Upload, PlusCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/utils/constants";

const UploadSection = ({ onParseQuoteFile, onManualAdd }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const parseQuoteFile = useCallback(async (file) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${API}/parse-quote-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.items.length === 0) {
        toast.error('No se encontraron elementos en el archivo');
      } else {
        toast.success(`Se encontraron ${response.data.items.length} elementos`);
        onParseQuoteFile(response.data);
      }
    } catch (error) {
      console.error("parse-quote-file failed:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const message = detail
        || (status ? `Error al parsear archivo (HTTP ${status})` : `Error de red al subir el archivo: ${error.message}`);
      toast.error(message, { duration: 8000 });
    } finally {
      setIsUploading(false);
    }
  }, [onParseQuoteFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await parseQuoteFile(files[0]);
    }
  }, [parseQuoteFile]);

  const handleFileSelect = useCallback(async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await parseQuoteFile(files[0]);
    }
  }, [parseQuoteFile]);

  return (
    <Card className="metric-card">
      <CardHeader className="border-b border-slate-100 p-4">
        <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Cargar Cotizaciones
        </CardTitle>
        <CardDescription className="text-slate-500">
          Sube archivos Excel con tus cotizaciones
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-800">
          El sistema detectará los elementos automáticamente, identificará diseños previamente guardados, y te pedirá asignar el nombre y material a cada uno.
        </div>

        <div
          data-testid="upload-dropzone"
          className={`upload-zone flex flex-col items-center justify-center ${isDragging ? 'active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="file-input"
          />
          
          {isUploading ? (
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Analizando archivo...</p>
            </div>
          ) : (
            <>
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mb-4" />
              <p className="text-slate-700 font-medium mb-2">
                Arrastra tu archivo aquí
              </p>
              <p className="text-slate-500 text-sm mb-4">
                o haz clic para seleccionar
              </p>
              <Badge variant="secondary" className="text-xs">
                Archivos de cotización Excel con dibujos
              </Badge>
            </>
          )}
        </div>
        
        {/* Manual Add Button */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <span className="text-sm text-slate-500">¿Archivo no reconocido?</span>
          <Button
            data-testid="manual-add-btn"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onManualAdd();
            }}
            className="flex items-center gap-1"
          >
            <PlusCircle className="w-4 h-4" />
            Añadir Manualmente
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UploadSection;
